import type { ReactElement, ReactNode } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface RailTooltipProps {
  /** What the item says: its label, hidden in the rail. */
  label: ReactNode;
  /**
   * Whether the tooltip shows: while the sidebar is collapsed, when the label
   * itself is hidden, and for the handle on the sidebar's edge, an icon alone,
   * also beside the full sidebar.
   */
  show: boolean;
  /** One focusable element (a link or a button), which keeps its own accessible name. */
  children: ReactElement;
}

/**
 * A sidebar item's label as a tooltip to its right, while the sidebar is a
 * rail (and for the handle on the sidebar's edge, an icon in both states).
 * The tooltip is always mounted and only its content comes and goes, so
 * collapsing never remounts the item (and never drops its focus). The item
 * keeps its own name (`sr-only` text where the label is hidden), so it is
 * named whether or not the tooltip shows.
 */
export function RailTooltip({ label, show, children }: RailTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {show && (
        <TooltipContent side="right" sideOffset={8}>
          {label}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
